import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './AxiosBaseQuery';
import type {
  ICartInformation,
  ICartItemPayload,
  ICartItemDetail,
} from '@/common/interfaces/cart.interface';
import { V3_APP_BASE_URL } from '@/constants/Config';

/** Fields that `updateCartItem` may optimistically patch on `getCartItemDetails`. */
type TOptimisticCartField = 'quantity' | 'price' | 'apparelSizes' | 'sizeQuantities';

/**
 * Per-field ownership for concurrent optimistic `updateCartItem` patches.
 * Key: `${cartItemId}:${field}` → request id that last wrote the field.
 * Used so a failed older request only rolls back fields it still owns.
 */
const optimisticCartFieldOwners = new Map<string, number>();
let nextOptimisticCartRequestId = 0;

function optimisticCartFieldKey(cartItemId: number, field: TOptimisticCartField): string {
  return `${cartItemId}:${field}`;
}

function claimOptimisticCartField(
  cartItemId: number,
  field: TOptimisticCartField,
  requestId: number
): void {
  optimisticCartFieldOwners.set(optimisticCartFieldKey(cartItemId, field), requestId);
}

function releaseOptimisticCartFieldIfOwner(
  cartItemId: number,
  field: TOptimisticCartField,
  requestId: number
): void {
  const key = optimisticCartFieldKey(cartItemId, field);
  if (optimisticCartFieldOwners.get(key) === requestId) {
    optimisticCartFieldOwners.delete(key);
  }
}

function isOptimisticCartFieldOwner(
  cartItemId: number,
  field: TOptimisticCartField,
  requestId: number
): boolean {
  return optimisticCartFieldOwners.get(optimisticCartFieldKey(cartItemId, field)) === requestId;
}

export const cartQuery = createApi({
  reducerPath: 'cartQuery',
  baseQuery: axiosBaseQuery({
    baseUrl: V3_APP_BASE_URL,
  }),
  tagTypes: ['Cart', 'CartCount'],
  endpoints: (build) => ({
    removeCartItem: build.mutation({
      query: ({ cartItemId }) => ({
        url: `api/cp/cart-item/${cartItemId}`,
        method: 'delete',
      }),
      invalidatesTags: ['CartCount'],
    }),
    updateCartItem: build.mutation({
      query: ({ cartItemId, data }) => ({
        url: `api/cp/cart-item/${cartItemId}`,
        method: 'put',
        data: { ...data },
      }),
      async onQueryStarted({ cartItemId, data }, { dispatch, queryFulfilled }) {
        nextOptimisticCartRequestId += 1;
        const requestId = nextOptimisticCartRequestId;
        let previousQuantity: number | undefined;
        let previousPrice: number | undefined;
        let previousApparelSizes: string | undefined;
        let previousSizeQuantities: ICartItemDetail['sizeQuantities'];
        let hasPreviousSizeQuantities = false;
        const ownedFields: TOptimisticCartField[] = [];
        let didPatch = false;

        dispatch(
          cartQuery.util.updateQueryData('getCartItemDetails', '', (draft) => {
            if (!draft?.cartItems?.length) return;

            const item = draft.cartItems.find(
              (cartItem: ICartItemDetail) => cartItem.id === cartItemId
            );
            if (!item) return;

            didPatch = true;

            if (data.quantity != null) {
              previousQuantity = item.quantity;
              item.quantity = data.quantity;
              claimOptimisticCartField(cartItemId, 'quantity', requestId);
              ownedFields.push('quantity');
            }
            if (data.price != null) {
              previousPrice = item.price;
              item.price = data.price;
              claimOptimisticCartField(cartItemId, 'price', requestId);
              ownedFields.push('price');
            }
            if (typeof data.apparelSizes === 'string') {
              previousApparelSizes = item.apparelSizes;
              item.apparelSizes = data.apparelSizes;
              claimOptimisticCartField(cartItemId, 'apparelSizes', requestId);
              ownedFields.push('apparelSizes');
            }
            if (data.sizeQuantities) {
              previousSizeQuantities = item.sizeQuantities
                ? { ...item.sizeQuantities }
                : item.sizeQuantities;
              hasPreviousSizeQuantities = true;
              item.sizeQuantities = { ...data.sizeQuantities };
              claimOptimisticCartField(cartItemId, 'sizeQuantities', requestId);
              ownedFields.push('sizeQuantities');
            }
          })
        );

        try {
          await queryFulfilled;
          if (!didPatch) {
            return;
          }
          ownedFields.forEach((field) => {
            releaseOptimisticCartFieldIfOwner(cartItemId, field, requestId);
          });
        } catch {
          if (!didPatch) {
            return;
          }

          // Request-scoped rollback: restore only fields this request still owns.
          dispatch(
            cartQuery.util.updateQueryData('getCartItemDetails', '', (draft) => {
              if (!draft?.cartItems?.length) return;

              const item = draft.cartItems.find(
                (cartItem: ICartItemDetail) => cartItem.id === cartItemId
              );
              if (!item) return;

              if (
                ownedFields.includes('quantity') &&
                isOptimisticCartFieldOwner(cartItemId, 'quantity', requestId) &&
                previousQuantity !== undefined
              ) {
                item.quantity = previousQuantity;
                releaseOptimisticCartFieldIfOwner(cartItemId, 'quantity', requestId);
              }
              if (
                ownedFields.includes('price') &&
                isOptimisticCartFieldOwner(cartItemId, 'price', requestId) &&
                previousPrice !== undefined
              ) {
                item.price = previousPrice;
                releaseOptimisticCartFieldIfOwner(cartItemId, 'price', requestId);
              }
              if (
                ownedFields.includes('apparelSizes') &&
                isOptimisticCartFieldOwner(cartItemId, 'apparelSizes', requestId) &&
                previousApparelSizes !== undefined
              ) {
                item.apparelSizes = previousApparelSizes;
                releaseOptimisticCartFieldIfOwner(cartItemId, 'apparelSizes', requestId);
              }
              if (
                ownedFields.includes('sizeQuantities') &&
                isOptimisticCartFieldOwner(cartItemId, 'sizeQuantities', requestId) &&
                hasPreviousSizeQuantities
              ) {
                item.sizeQuantities = previousSizeQuantities;
                releaseOptimisticCartFieldIfOwner(cartItemId, 'sizeQuantities', requestId);
              }
            })
          );
        }
      },
    }),
    updateCartItems: build.mutation({
      query: ({ clientId, data }) => ({
        url: `api/cp/cart-items/${clientId}`,
        method: 'put',
        data: { ...data },
      }),
    }),
    getCartItems: build.query({
      query: ({ cartItemId, countAndTotalOnly, isStore, limit, offset }) => {
        let url = 'api/cp/cart-item';
        if (cartItemId) {
          url += `/${cartItemId}`;
        }
        return {
          url,
          method: 'get',
          params: {
            countAndTotalOnly,
            isStore,
            limit,
            offset,
          },
        };
      },
      providesTags: (_result, _error, countAndTotalOnly) =>
        countAndTotalOnly ? ['CartCount'] : ['Cart'],
    }),
    addCartItem: build.mutation({
      query: ({ data }: { data: ICartItemPayload }) => ({
        url: 'api/cp/cart-item',
        method: 'post',
        data: { ...data },
      }),
      invalidatesTags: ['Cart'],
    }),
    removeStoreItem: build.mutation({
      query: ({ cartItemId }) => ({
        url: `api/cp/cart-item/${cartItemId}`,
        method: 'delete',
      }),
      invalidatesTags: ['Cart', 'CartCount'],
    }),
    getStoreItems: build.query<
      ICartInformation,
      { cartItemId?: string | number; isStore?: boolean; limit?: number; offset?: number }
    >({
      query: ({ cartItemId, isStore, limit, offset }) => {
        let url = 'api/cp/cart-item';
        if (cartItemId) {
          url += `/${cartItemId}`;
        }
        return {
          url,
          method: 'get',
          params: {
            isStore,
            limit,
            offset,
          },
        };
      },
      providesTags: ['Cart'],
    }),
    getCartItemDetails: build.query({
      query: () => ({
        url: 'api/cp/cart-item',
        method: 'get',
      }),
      providesTags: ['Cart'],
    }),
  }),
});

export const {
  useRemoveCartItemMutation,
  useUpdateCartItemMutation,
  useUpdateCartItemsMutation,
  useGetCartItemsQuery,
  useAddCartItemMutation,
  useGetStoreItemsQuery,
  useGetCartItemDetailsQuery,
  useRemoveStoreItemMutation,
} = cartQuery;
